import { useEffect } from "react";
import { CountdownBannerCtaProps } from "../types";
import CTALink from "@/components/CTA/ctaLink";
import { useInView } from "react-intersection-observer";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import EnrollLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import { Course } from "@/components/ProductCardListWrapper/variants/ProductCardListByQuery/docs/index.stories";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import PromoBannerTooltip from "@/components/PromoBanner/components/PromoBannerTooltip";
import useTranslations from "@/i18n/useTranslations";

export default function CountdownBannerCta({
  theme,
  header,
  subHeader,
  showSubheader,
  countdownTime,
  htmlId,
  location,
  name,
  position,
  showCTA,
  ctaElement,
  tooltip,
}: CountdownBannerCtaProps) {
  const t = useTranslations("Common");
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Banner,
      htmlId: htmlId,
      location: location,
      name: name,
      position: position,
    },
  });

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: htmlId,
      location: location,
      name: name,
      position: position,
    },
  });

  const { ref: bannerRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);
  if (!countdownTime) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="flex flex-col w-full lg:flex-row items-center bg-primary-500 text-white p-4 lg:px-8 lg:py-6 rounded-[8px] min-h-[150px]"
    >
      <div className="flex flex-col gap-y-4 lg:mr-4 mb-4 lg:mb-0 lg:text-left text-center flex-1">
        <span className="font-bold elm-h3">
          {sanitizeAndParseInlineHTML(header)}
        </span>
        {showSubheader && subHeader && (
          <span className="hidden lg:block font-normal elm-p3 gap-1 items-center">
            {sanitizeAndParseInlineHTML(subHeader)}
            {tooltip ? (
              <PromoBannerTooltip htmlContent={tooltip?.infoText} size={16} />
            ) : null}
          </span>
        )}
      </div>
      <div className="flex flex-col lg:flex-row min-h-20 items-center ">
        <div
          className={`flex flex-row items-center mb-4 lg:mb-0 py-2 lg:py-0 h-full ${theme === "light" ? "text-primary-500 bg-putty-100 rounded-xl p-4" : ""}`}
        >
          <span className="font-bold elm-h3 mr-1">{countdownTime.days}</span>
          <span className="elm-p3 lg:elm-p5">{t("days")}</span>
          <span className="mx-2 lg:mx-2 elm-h3 font-bold">:</span>
          <span className="font-bold elm-h3 mr-1">{countdownTime.hours}</span>
          <span className="elm-p3 lg:elm-p5">{t("hours")}</span>
          <span className="mx-2 lg:mx-2 elm-h3 font-bold">:</span>
          <span className="font-bold elm-h3 mr-1">{countdownTime.minutes}</span>
          <span className="elm-p3 lg:elm-p5">{t("minutes")}</span>
        </div>
        <div className="hidden lg:block self-stretch w-px bg-secondary-500 mx-14" />
        {showSubheader && subHeader && (
          <span className="lg:hidden font-normal mb-4 text-primary-foreground">
            {sanitizeAndParseInlineHTML(subHeader)}
            {tooltip ? (
              <PromoBannerTooltip htmlContent={tooltip?.infoText} size={16} />
            ) : null}
          </span>
        )}
        {showCTA && <div className="lg:mt-0">{ctaElement}</div>}
      </div>
    </div>
  );
}
